import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../CSS/SearchPage.css';

const searchablePages = [
  {
    title: 'Tactical Reports',
    path: '/reports',
    category: 'Reports',
    description: 'Search tactical reports, incident reports, and intelligence reporting.',
    keywords: ['reports', 'tactical reports', 'incident reports', 'intelligence', 'pdfs'],
  },
  {
    title: 'Situational Reports',
    path: '/spotlights',
    category: 'Reports',
    description: 'Situational reports and spotlight intelligence products.',
    keywords: ['spotlights', 'situational reports', 'reports', 'tactical reports'],
  },

  {
    title: 'Critical Insights',
    path: '/newcrit',
    category: 'Dashboard',
    description: 'Near-real-time civil unrest and critical insight dashboard.',
    keywords: ['critical insights', 'dashboard', 'civil unrest', 'risk', 'protests'],
  },
  {
    title: 'AR Risk Intelligence Hub',
    path: '/arhub',
    category: 'Risk Hub',
    description: 'Activist complaints, USDA inspections, and AAALAC accreditation tools.',
    keywords: ['ar hub', 'risk hub', 'activist', 'complaints', 'usda', 'aaalac'],
  },
  {
    title: 'Groups',
    path: '/orgs',
    category: 'Organizations',
    description: 'Search activist groups, organizations, profiles, and related reporting.',
    keywords: ['groups', 'organizations', 'orgs', 'profiles', 'activists'],
  },
  {
    title: 'Upcoming',
    path: '/upcoming',
    category: 'Calendar',
    description: 'Upcoming planned protests and future events.',
    keywords: ['upcoming', 'calendar', 'planned protests', 'events'],
  },
  {
    title: 'INA Video Library',
    path: '/videos',
    category: 'Media',
    description: 'Internal video library and media resources.',
    keywords: ['videos', 'media', 'library', 'training'],
  },
  {
    title: 'Activist Video Library',
    path: '/activistvideos',
    category: 'Media',
    description: 'Internal video library and media resources.',
    keywords: ['videos', 'media', 'library', 'training'],
  },
  {
    title: 'Security Planning',
    path: '/securityplanning',
    category: 'Planning',
    description: 'Security planning resources and related reports.',
    keywords: ['security planning', 'planning', 'risk assessment', 'security'],
  },
  {
    title: 'Threat Assessment Snapshot',
    path: '/threatassessment',
    category: 'Intelligence',
    description:
      'Concise threat assessments highlighting emerging risks, significant developments, and operational considerations affecting organizations, facilities, and targeted entities.',
    keywords: [
      'threat assessment',
      'threat snapshot',
      'risk assessment',
      'threat intelligence',
      'security threat',
      'operational risk',
      'emerging threats',
      'risk analysis',
      'security planning',
      'situational awareness',
      'threat monitoring',
      'threat report',
      'executive summary',
      'facility risk',
      'targeted entity',
    ],
  },
  {
    title: 'Annual Assessment',
    path: '/annualassessment',
    category: 'Strategic Intelligence',
    description:
      'Comprehensive yearly assessments examining trends, threat actors, campaign activity, operational impacts, and forecasts across key sectors and regions.',
    keywords: [
      'annual assessment',
      'annual report',
      'yearly assessment',
      'year in review',
      'strategic assessment',
      'trend analysis',
      'forecast',
      'annual intelligence',
      'annual threat assessment',
      'risk forecast',
      'industry outlook',
      'threat trends',
      'campaign analysis',
      'strategic intelligence',
      'executive assessment',
    ],
  },
  {
    title: 'Activist Nonprofit Financial Filings',
    path: '/financialfilings',
    category: 'Research',
    description:
      'Search and review nonprofit financial filings, tax documents, organizational disclosures, and funding information related to activist organizations and advocacy groups.',
    keywords: [
      'financial filings',
      'nonprofit filings',
      'irs filings',
      'form 990',
      '990',
      'tax documents',
      'financial reports',
      'funding',
      'donations',
      'revenue',
      'expenses',
      'activist funding',
      'nonprofit research',
      'organization finances',
      'charity filings',
      'grant funding',
      'financial transparency',
      'advocacy groups',
      'nonprofit database',
      'financial records',
    ],
  },
];

const normalize = (value) => String(value ?? '').toLowerCase();

const SearchPage = () => {
  const [searchParams] = useSearchParams();

const [searchTerm, setSearchTerm] = useState(
  searchParams.get('q') || ''
);
  const [siteData, setSiteData] = useState({
    reports: [],
    orgs: [],
    spotlights: [],
  });

  useEffect(() => {
    fetch('/api/hello', {
      credentials: 'include',
      cache: 'no-store',
    })
      .then((res) => res.json())
      .then((data) => {
        setSiteData({
          reports: data.reports || [],
          orgs: data.orgs || [],
          spotlights: data.Spotlight || [],
        });
      })
      .catch((err) => console.error('Error loading search data:', err));
  }, []);

  const openReport = (linkUrl, reportType = 'Search') => {
    const userEmail = localStorage.getItem('user-email');

    if (!userEmail) {
      alert('You must be logged in to view this report.');
      return;
    }

    const filename = linkUrl.split('/').pop();
    const reportUrl = `/api/reports/${encodeURIComponent(filename)}?email=${encodeURIComponent(userEmail)}`;

    fetch('/api/log-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        reportName: filename,
        reportType,
        userEmail,
      }),
    }).catch(() => {});

    window.open(reportUrl, '_blank');
  };

  const pageResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return searchablePages;

    return searchablePages.filter((page) => {
      const searchableText = [
        page.title,
        page.category,
        page.description,
        ...(page.keywords || []),
      ]
        .join(' ')
        .toLowerCase();

      return searchableText.includes(term);
    });
  }, [searchTerm]);

  const groupResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];

    return siteData.orgs
      .filter((org) => {
        const fields = org.fields || {};

        return [
          fields.Title,
          fields.AKA,
          fields.OldName,
          fields.Sector,
        ]
          .join(' ')
          .toLowerCase()
          .includes(term);
      })
      .slice(0, 20);
  }, [searchTerm, siteData.orgs]);

  const spotlightResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];

    return siteData.spotlights
      .filter((item) =>
        normalize(item.name || item.originalName).includes(term)
      )
      .slice(0, 20);
  }, [searchTerm, siteData.spotlights]);

  const reportResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];

    return siteData.reports
      .filter((report) => {
        const fields = report.fields || {};

        if (fields.field_9 === 'Protest Planned') return false;
        if (fields.DM === true) return false;

        const sector = Array.isArray(fields.field_6)
          ? fields.field_6.join(' ')
          : fields.field_6;

        const groupResponsibleLU = Array.isArray(fields.GroupResponsibleLU)
          ? fields.GroupResponsibleLU.map((item) => item.LookupValue).join(' ')
          : '';

        return [
          fields.Title,
          fields.field_9,
          fields.field_10,
          groupResponsibleLU,
          fields.field_11,
          fields.Region0,
          fields.field_3,
          fields.field_4,
          fields.field_5,
          sector,
        ]
          .join(' ')
          .toLowerCase()
          .includes(term);
      })
      .slice(0, 20);
  }, [searchTerm, siteData.reports]);

  const totalResults =
    pageResults.length +
    groupResults.length +
    spotlightResults.length +
    reportResults.length;

  const hasSearch = searchTerm.trim().length > 0;

  return (
    <div className="site-search-page">
      <div className="site-search-hero">
        <div className="site-search-text">
  <p className="site-search-eyebrow">Orion Navigation</p>
  <h1>Search</h1>
  <p className="site-search-description">
    Search site tools, groups, situational reports, and tactical report metadata.
  </p>
</div>

        <div className="site-search-count">
          <span>{totalResults}</span>
          <p>Matches</p>
        </div>
      </div>

      <div className="site-search-box">
        <input
          type="text"
          placeholder="Search pages, groups, spotlights, reports..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
      </div>

      <SearchSection title="Page Matches" count={pageResults.length}>
        {pageResults.map((page) => (
          <a href={page.path} className="site-search-card" key={page.path}>
            <div>
              <p className="site-search-category">{page.category}</p>
              <h2>{page.title}</h2>
              <p>{page.description}</p>
            </div>
            <span className="site-search-arrow">→</span>
          </a>
        ))}
      </SearchSection>

      {hasSearch && (
        <>
          <SearchSection title="Groups" count={groupResults.length}>
            {groupResults.map((org) => {
              const fields = org.fields || {};
              const orgId = fields.id || org.id;

              return (
                <a
                  href={`/orgdetails/${orgId}`}
                  className="site-search-card"
                  key={`org-${orgId}`}
                >
                  <div>
                    <p className="site-search-category">Group</p>
                    <h2>{fields.Title}</h2>
                    <p>
                      {[fields.Sector, fields.AKA ? `AKA: ${fields.AKA}` : '', fields.OldName ? `Prior Name: ${fields.OldName}` : '']
                        .filter(Boolean)
                        .join(' • ')}
                    </p>
                  </div>
                  <span className="site-search-arrow">→</span>
                </a>
              );
            })}
          </SearchSection>

          <SearchSection title="Spotlights" count={spotlightResults.length}>
            {spotlightResults.map((item, index) => {
              const fileName = item.name || item.originalName || '';
              const displayName = item.originalName || item.name || 'Spotlight';

              return (
                <a
                  href={`/api/highlights/Spotlight/${encodeURIComponent(fileName)}.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="site-search-card"
                  key={`spotlight-${item.id || fileName || index}`}
                >
                  <div>
                    <p className="site-search-category">Spotlight</p>
                    <h2>{displayName}</h2>
                    <p>Situational report / spotlight intelligence product.</p>
                  </div>
                  <span className="site-search-arrow">↗</span>
                </a>
              );
            })}
          </SearchSection>

          <SearchSection title="Tactical Reports" count={reportResults.length}>
            {reportResults.map((report) => {
              const fields = report.fields || {};
              const sector = Array.isArray(fields.field_6)
                ? fields.field_6.join(', ')
                : fields.field_6 || '';

              return (
                <button
                  type="button"
                  className="site-search-card site-search-button-card"
                  key={`report-${fields.id || report.id}`}
                  onClick={() => openReport(fields.Links1)}
                >
                  <div>
                    <p className="site-search-category">Tactical Report</p>
                    <h2>{fields.Title}</h2>
                    <p>
                      {[
                        fields.field_1_formatted,
                        fields.field_9,
                        [fields.field_5, fields.field_4, fields.field_3].filter(Boolean).join(', '),
                        sector,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </p>
                  </div>
                  <span className="site-search-arrow">↗</span>
                </button>
              );
            })}
          </SearchSection>
        </>
      )}

      {totalResults === 0 && (
        <div className="site-search-empty">
          No matching pages, groups, spotlights, or reports found.
        </div>
      )}
    </div>
  );
};

const SearchSection = ({ title, count, children }) => {
  if (!children || count === 0) return null;

  return (
    <div className="site-search-section">
      <div className="site-search-section-header">
        <h2>{title}</h2>
        <span>{count}</span>
      </div>

      <div className="site-search-results">
        {children}
      </div>
    </div>
  );
};

export default SearchPage;